import { Button, Card, Space, Typography } from 'antd';
import { useState } from 'react';
import { findNextInventorizationRegisterPhotoRef } from '@/features/inventorization/domain/inventorization-registers';
import { INVENTORIZE_PHOTO_REVIEW_FIELD_SLOT_IDS } from '@/features/inventorization/domain/review/inventorize-review-field-ids';
import type { InventorizeReviewField } from '@/features/inventorization/domain/review/inventorize-review-fields';
import { photoWizardText } from '@/features/inventorization/photos/photo-wizard-i18n';
import { usePhotoUrls } from '@/features/inventorization/photos/usePhotoUrls';
import {
  PhotoPreviewModal,
  PhotoWizardPhotoGrid,
} from '@/routers/inventorization/components/photos/InventorizePhotoWizard';
import { createCapturedPhotoAssetFromFile } from '@/shared/camera/browser-photo-capture';
import type { CapturedPhotoAsset } from '@/shared/camera/photo-capture-adapter';
import { type CaptureSession, type LocalPhotoRef, PHOTO_CONTEXTS } from '@/shared/capture/capture-session';
import {
  getInventorizationPhotosForSlot,
  type PhotoSlotDefinition,
  readInventorizationPhotoSlot,
} from '@/shared/capture/inventorization-photo-slots';

export function PhotoCategoryFieldWizard({
  field,
  isCapturingSlotId,
  onBack,
  onCapturePhoto,
  onDeletePhoto,
  onRefresh,
  session,
}: {
  field: InventorizeReviewField;
  isCapturingSlotId: string | null;
  onBack: () => void;
  onCapturePhoto: (
    slot: PhotoSlotDefinition,
    asset: CapturedPhotoAsset,
    replacedPhoto?: LocalPhotoRef,
  ) => Promise<unknown>;
  onDeletePhoto: (photo: LocalPhotoRef) => Promise<unknown>;
  onRefresh: () => Promise<unknown> | unknown;
  session: CaptureSession;
}) {
  const [selectedPhoto, setSelectedPhoto] = useState<LocalPhotoRef | null>(null);
  const slot = getPhotoFieldSlot(field.id);
  const photos = slot
    ? getInventorizationPhotosForSlot(session.photos, slot).filter(photo => !photo.uploadedRemoteId)
    : [];
  const urls = usePhotoUrls(selectedPhoto ? [selectedPhoto] : photos);
  const selectedPhotoUrl = selectedPhoto ? urls[selectedPhoto.id]?.originalUrl : null;

  async function handleFile(file: File | undefined, replacedPhoto?: LocalPhotoRef) {
    if (!(file && slot)) {
      return;
    }

    const asset = await createCapturedPhotoAssetFromFile(file);
    await onCapturePhoto(replacedPhoto ? slot : createPhotoFieldCaptureSlot(slot, photos), asset, replacedPhoto);
    await onRefresh();
  }

  return (
    <Card>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <Typography.Text type="secondary">{photoWizardText('eyebrow', 'Photos')}</Typography.Text>
          <Typography.Title level={3} style={{ marginTop: 0 }}>
            {field.label}
          </Typography.Title>
          <Typography.Paragraph type="secondary">
            {photoWizardText('category.description', 'Review, delete, or add photos for this category.')}
          </Typography.Paragraph>
        </div>

        {slot ? (
          <PhotoWizardPhotoGrid
            isCapturingSlotId={isCapturingSlotId}
            onDelete={photo => void onDeletePhoto(photo).then(onRefresh)}
            onFile={(file, replacedPhoto) => void handleFile(file, replacedPhoto)}
            onSelectPhoto={setSelectedPhoto}
            photos={photos}
            photoUrls={urls}
            slot={slot}
          />
        ) : null}

        <Space style={{ display: 'flex', justifyContent: 'space-between' }} wrap>
          <Button onClick={onBack}>{photoWizardText('actions.backToReview', 'Back to review')}</Button>
          <Button onClick={onBack} type="primary">
            {photoWizardText('actions.saveField', 'Save field')}
          </Button>
        </Space>

        <PhotoPreviewModal
          onClose={() => setSelectedPhoto(null)}
          open={Boolean(selectedPhoto)}
          photoUrl={selectedPhotoUrl ?? null}
          title={field.label}
        />
      </Space>
    </Card>
  );
}

function getPhotoFieldSlot(fieldId: string) {
  const slotId =
    INVENTORIZE_PHOTO_REVIEW_FIELD_SLOT_IDS[fieldId as keyof typeof INVENTORIZE_PHOTO_REVIEW_FIELD_SLOT_IDS];

  return readInventorizationPhotoSlot(slotId);
}

function createPhotoFieldCaptureSlot(slot: PhotoSlotDefinition, photos: LocalPhotoRef[]): PhotoSlotDefinition {
  if (slot.context !== PHOTO_CONTEXTS.register) {
    return slot;
  }

  const registerId = findNextInventorizationRegisterPhotoRef(photos);

  return {
    ...slot,
    id: `${slot.id}-${registerId}`,
    registerId,
  };
}
